import { gql, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { SubStepTypes, type User } from '@generated/graphql';
import { type CustomField, FETCH_CUSTOM_FIELDS } from '@interfaces/customField';
import type { WorkflowCompletion } from '@interfaces/workflow';
import { getWorkflowCompletionHeaders } from '@ovComponents/4-module/workflowCompletion/workflowCompletionHeaders';
import { UserContext } from '@providers/userContextProvider';
import { modifyUpdatingCustomFields } from '@utils/modifyUpdatingCustomFields';
import { useSnackbar } from 'hephaestus/web';
import { isEmpty, isNull, isUndefined } from 'lodash-es';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../../../../hooks/usePermission';
import {
  getEnabledCustomFields,
  type WorkflowCustomFieldOption,
} from '../utils';
import { IncomeAssetsAndDebtsVisual } from './incomeAssetsAndDebts.visual';
import {
  shouldEnableDebtAssociated,
  shouldEnableSourceOfWealthAndFunds,
} from './utils';

const FETCH_USER = (derivedCustomFields?: string[]) => gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        type
        annualIncomeCents
        annualIncomeStr
        financialLiquidAssetsCents
        financialFixedAssetsCents
        totalDebtCents
        annualDebtPaymentsCents
        debtAssociated
        isAccreditedInvestor
        sourceOfWealth
        sourceOfFunds
        uniqueCircumstances
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format}
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
      }
    }
  }
`;

const INITIAL_USER_DATA: Partial<User> = {
  type: undefined,
  annualIncomeCents: undefined,
  annualIncomeStr: undefined,
  financialLiquidAssetsCents: undefined,
  financialFixedAssetsCents: undefined,
  totalDebtCents: undefined,
  annualDebtPaymentsCents: undefined,
  debtAssociated: [],
  isAccreditedInvestor: undefined,
  sourceOfWealth: [],
  sourceOfFunds: [],
  uniqueCircumstances: undefined,
  customFields: [],
};

type CustomFieldValueInput =
  | { key?: string | null; value?: unknown }
  | null
  | undefined;

const normalizeCustomFields = (
  fields?: unknown,
): Array<{ key: string; value?: unknown }> => {
  if (!Array.isArray(fields)) return [];

  return (fields as CustomFieldValueInput[])
    .filter((field): field is { key?: string | null; value?: unknown } =>
      Boolean(field && typeof field === 'object'),
    )
    .map(({ key, value }) => ({
      key: key ?? '',
      value,
    }))
    .filter((field) => field.key !== '');
};

type IncomeAssetsAndDebtsProps = {
  options?: Record<string, WorkflowCustomFieldOption>;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: WorkflowCompletion | null;
  grid?: boolean;
  updateMode?: boolean;
};

export const IncomeAssetsAndDebts = ({
  options,
  userId,
  onNext,
  stepLoading,
  workflowCompletion,
  grid = false,
  updateMode = false,
}: IncomeAssetsAndDebtsProps) => {
  const { t } = useTranslation(['shared']);
  const { enqueueSnackbar } = useSnackbar();
  const [derivedCustomFields, setDerivedCustomFields] =
    useState<CustomField[]>();
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] =
    useState<string[]>();
  const enabledCustomFields = getEnabledCustomFields(options);
  const { activeOrganization } = useContext(UserContext);
  const canReadCustomFields = usePermission('read:custom_fields');

  const customFieldsPermitted =
    canReadCustomFields && enabledCustomFields.length !== 0;

  const [userData, setUserData] = useState(INITIAL_USER_DATA);

  // loading 1 - custom fields
  const { loading: customFieldsLoading } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          workflowStep: SubStepTypes.IncomeAssetsAndDebts,
          organizationId: activeOrganization?.id ?? undefined,
          keys: !isEmpty(enabledCustomFields) ? enabledCustomFields : undefined,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !customFieldsPermitted,
    onCompleted: (res: {
      fetchCustomFields?: { customFields?: CustomField[] };
    }) => {
      const customFields = res.fetchCustomFields?.customFields;
      setDerivedCustomFields(customFields);
      const customFieldKeys = customFields?.map(
        (customField) => customField.key,
      );
      setDerivedCustomFieldsKeys(customFieldKeys);
    },
  });

  // loading 2 - user's data
  const { data, loading } = useQuery(FETCH_USER(derivedCustomFieldsKeys), {
    variables: { userId },
    fetchPolicy: 'no-cache',
    skip: customFieldsLoading,
  });

  const [updateUser, { loading: updateUserLoading }] = useMutation(
    UPDATE_USER,
    {
      context: {
        headers: getWorkflowCompletionHeaders(workflowCompletion),
      },
      onCompleted: () => {
        if (updateMode) {
          enqueueSnackbar(t('profileDetailsUpdated'), { variant: 'success' });
        }
        onNext();
      },
      variables: {
        input: {
          ...userData,
          sourceOfFunds: shouldEnableSourceOfWealthAndFunds(
            userData.annualIncomeCents,
            userData.financialLiquidAssetsCents,
            userData.financialFixedAssetsCents,
          )
            ? userData.sourceOfFunds
            : [],
          sourceOfWealth: shouldEnableSourceOfWealthAndFunds(
            userData.annualIncomeCents,
            userData.financialLiquidAssetsCents,
            userData.financialFixedAssetsCents,
          )
            ? userData.sourceOfWealth
            : [],
          debtAssociated: shouldEnableDebtAssociated(
            userData.totalDebtCents,
            userData.annualDebtPaymentsCents,
          )
            ? userData.debtAssociated
            : [],
          uniqueCircumstances:
            typeof userData.uniqueCircumstances === 'string' &&
            !isEmpty(userData.uniqueCircumstances)
              ? userData.uniqueCircumstances
              : undefined,
          isAccreditedInvestor:
            typeof userData.isAccreditedInvestor === 'boolean',
          ...(enabledCustomFields &&
          derivedCustomFields &&
          userData?.customFields
            ? {
                customFields: modifyUpdatingCustomFields(
                  normalizeCustomFields(userData.customFields),
                  derivedCustomFields,
                  derivedCustomFieldsKeys,
                ),
              }
            : {}),
          userId,
        },
      },
    },
  );

  const handleCentsData = useCallback((value?: number | null) => {
    if (isUndefined(value) || isNull(value)) return undefined;
    return value ?? undefined;
  }, []);

  useEffect(() => {
    if (data) {
      setUserData({
        type: data.fetchUser.user.type,
        annualIncomeCents: handleCentsData(
          data.fetchUser.user.annualIncomeCents,
        ),
        annualIncomeStr: data.fetchUser.user.annualIncomeStr || undefined,
        financialLiquidAssetsCents: handleCentsData(
          data.fetchUser.user.financialLiquidAssetsCents,
        ),
        financialFixedAssetsCents: handleCentsData(
          data.fetchUser.user.financialFixedAssetsCents,
        ),
        totalDebtCents: handleCentsData(data.fetchUser.user.totalDebtCents),
        annualDebtPaymentsCents: handleCentsData(
          data.fetchUser.user.annualDebtPaymentsCents,
        ),
        debtAssociated: data.fetchUser.user.debtAssociated || [],
        isAccreditedInvestor:
          typeof data.fetchUser.user.isAccreditedInvestor === 'boolean'
            ? data.fetchUser.user.isAccreditedInvestor
            : undefined,
        sourceOfWealth: data.fetchUser.user.sourceOfWealth || [],
        sourceOfFunds: data.fetchUser.user.sourceOfFunds || [],
        uniqueCircumstances:
          data.fetchUser.user.uniqueCircumstances || undefined,
        customFields: data.fetchUser.user.customFields || [],
      });
    }
  }, [data, handleCentsData]);

  return (
    <IncomeAssetsAndDebtsVisual
      options={options}
      userData={userData}
      updateUser={setUserData}
      continueFunc={updateUser}
      loading={
        loading || updateUserLoading || stepLoading || customFieldsLoading
      }
      grid={grid}
      updateMode={updateMode}
      workflowCompletion={workflowCompletion}
      activeCustomFields={derivedCustomFieldsKeys}
      derivedCustomFields={derivedCustomFields}
    />
  );
};

export default IncomeAssetsAndDebts;
